import api from './api';

/**
 * 📋 SERVICE RECHERCHES SAUVEGARDÉES
 * 
 * Interface avec l'API backend pour gérer les recherches persistantes
 */
class SavedSearchService {

  /**
   * 💾 Sauvegarder une nouvelle recherche
   */
  async saveSearch(searchData) {
    try {
      console.log('📋 [SavedSearchService] Saving search:', {
        name: searchData.name,
        hasFilters: !!searchData.filters,
        filtersKeys: searchData.filters ? Object.keys(searchData.filters) : []
      });

      const response = await api.post('/saved-searches', {
        name: searchData.name,
        filters: searchData.filters,
        description: searchData.description,
        isDefault: searchData.isDefault || false
      });

      console.log('✅ [SavedSearchService] Search saved successfully:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ [SavedSearchService] Save search error:', error);
      throw this._handleError(error, 'Erreur lors de la sauvegarde de la recherche');
    }
  }

  /**
   * 📋 Obtenir les recherches sauvegardées de l'utilisateur
   */
  async getUserSavedSearches(options = {}) {
    try {
      const { limit = 20, orderBy = 'lastUsedAt' } = options;
      
      console.log('📋 [SavedSearchService] Fetching user saved searches:', { limit, orderBy });

      const response = await api.get('/saved-searches', {
        params: { limit, orderBy }
      });

      console.log('✅ [SavedSearchService] Searches retrieved:', {
        count: response.data.data?.length || 0
      });

      return response.data;

    } catch (error) {
      console.error('❌ [SavedSearchService] Get searches error:', error);
      throw this._handleError(error, 'Erreur lors de la récupération des recherches');
    }
  }

  /**
   * 🔄 Charger une recherche sauvegardée
   */
  async loadSearch(searchId) {
    try {
      console.log('📋 [SavedSearchService] Loading search:', searchId);

      const response = await api.get(`/saved-searches/${searchId}`);

      console.log('✅ [SavedSearchService] Search loaded:', {
        id: response.data.data?.id,
        name: response.data.data?.name
      });

      return response.data;

    } catch (error) {
      console.error('❌ [SavedSearchService] Load search error:', error);
      throw this._handleError(error, 'Erreur lors du chargement de la recherche');
    }
  }

  /**
   * 🗑️ Supprimer une recherche sauvegardée
   */
  async deleteSearch(searchId) {
    try {
      console.log('📋 [SavedSearchService] Deleting search:', searchId);

      const response = await api.delete(`/saved-searches/${searchId}`);

      console.log('✅ [SavedSearchService] Search deleted successfully');
      return response.data;

    } catch (error) {
      console.error('❌ [SavedSearchService] Delete search error:', error);
      throw this._handleError(error, 'Erreur lors de la suppression de la recherche');
    }
  }

  /**
   * ⭐ Définir comme recherche par défaut
   */
  async setAsDefault(searchId) {
    try {
      console.log('📋 [SavedSearchService] Setting as default:', searchId);

      const response = await api.put(`/saved-searches/${searchId}/default`);

      console.log('✅ [SavedSearchService] Search set as default successfully');
      return response.data;

    } catch (error) {
      console.error('❌ [SavedSearchService] Set default error:', error);
      throw this._handleError(error, 'Erreur lors de la définition par défaut');
    }
  }

  /**
   * 🎯 Obtenir la recherche par défaut
   */
  async getDefaultSearch() {
    try {
      console.log('📋 [SavedSearchService] Getting default search');

      const response = await api.get('/saved-searches/default');

      console.log('✅ [SavedSearchService] Default search retrieved:', {
        id: response.data.data?.id,
        name: response.data.data?.name
      });

      return response.data;

    } catch (error) {
      // 404 est normal si aucune recherche par défaut n'est définie
      if (error.response?.status === 404) {
        console.log('ℹ️ [SavedSearchService] No default search found');
        return null;
      }

      console.error('❌ [SavedSearchService] Get default search error:', error);
      throw this._handleError(error, 'Erreur lors de la récupération de la recherche par défaut');
    }
  }

  /**
   * 🔄 Appliquer une recherche sauvegardée (charger et marquer comme utilisée)
   */
  async applySearch(searchId) {
    try {
      const result = await this.loadSearch(searchId);
      
      // La requête load marque automatiquement la recherche comme utilisée
      return result.data; // Retourner directement les données de la recherche

    } catch (error) {
      console.error('❌ [SavedSearchService] Apply search error:', error);
      throw error;
    }
  }

  /**
   * 🛠️ Gestion des erreurs centralisée
   */
  _handleError(error, defaultMessage) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    
    if (error.message) {
      return new Error(error.message);
    }
    
    return new Error(defaultMessage);
  }

  /**
   * 🎨 Formatage des filtres pour l'affichage
   */
  formatFiltersForDisplay(filters) {
    const activeFilters = [];

    if (filters.categoryIds && filters.categoryIds.length > 0) {
      activeFilters.push(`${filters.categoryIds.length} catégorie(s)`);
    }

    if (filters.cities && filters.cities.length > 0) {
      activeFilters.push(`${filters.cities.length} ville(s)`);
    }

    if (filters.country) {
      activeFilters.push(`Pays: ${filters.country}`);
    }

    if (filters.minPrice || filters.maxPrice) {
      const priceRange = [];
      if (filters.minPrice) priceRange.push(`Min: ${filters.minPrice}€`);
      if (filters.maxPrice) priceRange.push(`Max: ${filters.maxPrice}€`);
      activeFilters.push(priceRange.join(' - '));
    }

    if (filters.isAvailable !== undefined) {
      activeFilters.push(filters.isAvailable ? 'Disponible' : 'Non disponible');
    }

    if (filters.isVerified !== undefined) {
      activeFilters.push(filters.isVerified ? 'Vérifié' : 'Non vérifié');
    }

    return activeFilters.length > 0 ? activeFilters.join(', ') : 'Aucun filtre';
  }

  /**
   * 📊 Calculer le hash des filtres pour détecter les changements
   */
  calculateFiltersHash(filters) {
    // Nettoyer et normaliser les filtres pour le hash
    const cleanFilters = {};
    
    Object.keys(filters || {}).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '' && 
          !(Array.isArray(value) && value.length === 0)) {
        cleanFilters[key] = value;
      }
    });

    return JSON.stringify(cleanFilters, Object.keys(cleanFilters).sort());
  }
}

export default new SavedSearchService();
